import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Forward as ForwardIcon, Hash, Paperclip, X } from 'lucide-react';
import clsx from 'clsx';
import { forwardOf, type ForwardSnapshot, type Message } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmt } from '@/lib/format';
import { useMembers, useWorkspaceId } from '@/lib/hooks';
import { qk } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Avatar, Button, Field, Input, Modal, Textarea } from '@/components/ui';
import { PersonButton } from '@/features/people/PersonDrawer';
import { MessageBody } from './MessageBody';

type Target = { kind: 'person'; id: string; label: string } | { kind: 'channel'; id: string; label: string };

/** t136: the quoted original inside a forwarded message (also the preview in the Forward dialog). */
export function ForwardCard({ f, preview }: { f: ForwardSnapshot; preview?: boolean }) {
  return (
    <div className="mt-1 max-w-2xl rounded-lg border-l-[3px] border-slate-400 bg-slate-50 px-3 py-2 dark:border-slate-500 dark:bg-slate-800/60" data-testid="forward-card">
      <div className="mb-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
        <ForwardIcon className="h-3.5 w-3.5" />Forwarded from {f.from_label}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <PersonButton userId={f.sender_id} name={f.sender_name} className="flex items-center gap-1.5 font-semibold hover:underline">
          <Avatar size={20} name={f.sender_name} id={f.sender_id ?? f.sender_name} />{f.sender_name}
        </PersonButton>
        <span className="text-xs text-slate-500">{fmt.dateTime(f.sent_at)}</span>
      </div>
      <div className={clsx('mt-0.5', preview && 'max-h-40 overflow-y-auto')}><MessageBody body={f.body} /></div>
      <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-500">
        {f.attachment_count > 0 && <span className="inline-flex items-center gap-1"><Paperclip className="h-3 w-3" />{f.attachment_count} attachment{f.attachment_count > 1 ? 's' : ''} not forwarded</span>}
        {!preview && <Link className="link" to={`/c/${f.conversation_id}#m-${f.message_id}`}>View original</Link>}
      </div>
    </div>
  );
}

/** Snapshot for the dialog preview, from the message being forwarded. */
function snapshotOf(m: Message, from: string, senderName: string): ForwardSnapshot {
  return forwardOf(m) ?? { message_id: m.id, conversation_id: m.conversation_id, from_label: from, sender_id: m.sender_id, sender_name: senderName, sent_at: m.created_at, body: m.body, attachment_count: m.attachments?.length ?? 0, has_comment: false };
}

export function ForwardModal({ message, fromLabel, onClose }: { message: Message; fromLabel: string; onClose: () => void }) {
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const { members, byId } = useMembers();
  const toast = useUi(s => s.toast);
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [to, setTo] = useState<Target | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const convs = useQuery({ queryKey: qk.conversations(ws ?? ''), queryFn: () => api.conversations(ws!), enabled: Boolean(ws), staleTime: 60_000 });
  const options = useMemo<Target[]>(() => {
    const t = q.trim().toLowerCase();
    const people: Target[] = members.filter(p => p.user_id !== userId && (p.status ?? 'active') === 'active' && (!t || p.display_name.toLowerCase().includes(t)))
      .slice(0, 6).map(p => ({ kind: 'person', id: p.user_id, label: p.display_name }));
    const channels: Target[] = (convs.data?.conversations ?? []).filter(c => c.kind === 'channel' && c.is_member && !c.archived_at && c.id !== message.conversation_id
      && (!t || `${c.slug ?? ''} ${c.title ?? ''}`.toLowerCase().includes(t))).slice(0, 6).map(c => ({ kind: 'channel', id: c.id, label: `#${c.slug ?? c.title}` }));
    return [...people, ...channels];
  }, [q, members, userId, convs.data, message.conversation_id]);
  const snap = snapshotOf(message, fromLabel, byId.get(message.sender_id ?? '')?.display_name ?? 'Someone');

  const send = async () => {
    if (!to) return;
    setBusy(true);
    try {
      const r = await api.forwardMessage(message.id, { client_message_id: crypto.randomUUID(), comment: comment.trim() || undefined, ...(to.kind === 'person' ? { user_id: to.id } : { conversation_id: to.id }) });
      toast(`Forwarded to ${to.label}`, 'success', { label: 'Open', run: () => nav(`/c/${r.conversation_id}#m-${r.id}`) });
      onClose();
    } catch (e) { toast(errorMessage(e), 'error'); }
    setBusy(false);
  };

  return (
    <Modal open onClose={onClose} title="Forward message" footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => void send()} loading={busy} disabled={!to} data-testid="forward-send">Forward</Button></>}>
      <div className="space-y-3" data-testid="forward-modal">
        <Field label="To">
          {to ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-50 py-0.5 pl-1 pr-2 text-sm dark:border-slate-600 dark:bg-slate-800" data-testid="forward-to-chip">
              {to.kind === 'person' ? <Avatar size={20} name={to.label} id={to.id} /> : <Hash className="h-4 w-4" />}{to.label.replace(/^#/, '')}
              <button type="button" aria-label="Remove" onClick={() => setTo(null)}><X className="h-3.5 w-3.5" /></button>
            </span>
          ) : (
            <div>
              <Input autoFocus placeholder="Search people and channels" value={q} onChange={e => setQ(e.target.value)} data-testid="forward-search" />
              <div className="mt-1 max-h-56 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700">
                {options.map(o => (
                  <button key={`${o.kind}:${o.id}`} type="button" onClick={() => setTo(o)} data-testid="forward-option"
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800">
                    {o.kind === 'person' ? <Avatar size={22} name={o.label} id={o.id} /> : <Hash className="h-4 w-4 text-slate-500" />}
                    <span className="font-medium">{o.label.replace(/^#/, '')}</span>
                    <span className="ml-auto text-xs text-slate-500">{o.kind === 'person' ? 'Direct message' : 'Channel'}</span>
                  </button>
                ))}
                {!options.length && <div className="px-3 py-2 text-sm text-slate-500">No one found</div>}
              </div>
            </div>
          )}
        </Field>
        <Field label="Add a message (optional)"><Textarea rows={2} value={comment} onChange={e => setComment(e.target.value)} maxLength={4000} data-testid="forward-comment" /></Field>
        <ForwardCard f={snap} preview />
      </div>
    </Modal>
  );
}
